<template>
    <div
        class="article-page"
        :style="[
            styleProps,
            isRtlLanguageFunc(this.$globalConstants.host.language_code),
        ]"
    >
        <main :style="{ 'max-width': pageWidth }">
            <div class="article-nav">
                <div class="nav-links">
                    <SEOContentBreadcrumb
                        v-if="showBreadcrumbs && $route.path !== '/'"
                        color="#333"
                        :articleTitle="article.title"
                    ></SEOContentBreadcrumb>
                </div>
            </div>

            <div
                v-if="article?.paragraphs"
                class="article-content"
                :style="layoutStyles"
            >
                <div class="main-column">
                    <AdsenseHeader
                        v-if="
                            article.hero_image ||
                            $globalConstants.host.hero_banner_gw_deluxe
                        "
                        :title="article.title"
                        :titleColor="titleColor"
                        :backgroundImage="articleHeroImage"
                        :article-author="articleAuthor"
                    />

                    <a
                        v-if="article?.logo_image"
                        class="review-logo"
                        :href="article?.cta_button"
                        target="_blank"
                        rel="nofollow"
                    >
                        <img
                            :src="`${$globalConstants.imageUrl}/${article.logo_image}?d=400x300&q=80`"
                            alt="logo"
                        />
                    </a>

                    <DateDisplay
                        v-if="!articleAuthor"
                        :text-color="dateTextColor"
                        :text-size="
                            isMobile()
                                ? dateTextSizeMobile
                                : dateTextSizeDesktop
                        "
                        style="padding: 0 0 20px 0"
                    />

                    <div
                        class="main-partial-container"
                        v-if="article?.paragraphs.length"
                        :style="{
                            '--cta-color': ctaButtonColor,
                            color: textColor,
                        }"
                    >
                        <PartialHandler
                            v-for="(partial, i) in partialsArray"
                            :partial-length="partialsArray.length"
                            :partial-types="partial"
                            :providers="rankingProviders"
                            :contentsItems="article?.sub_headers"
                            :alt-text="articlePrimaryKeyword"
                            :key="i"
                            :index="i"
                            @loadNextPartials="partialCount += 5"
                        />
                    </div>

                    <div class="social-links">
                        <p>{{ $t('Was this helpful? Share it on') }}</p>
                        <SocialMediaLinks
                            v-if="article.title && this.showSocialLinks"
                            :pageTitle="article.title"
                        ></SocialMediaLinks>
                    </div>

                    <BackToTop />

                    <SeoArticleList
                        style="margin-top: 40px"
                        v-if="article?.paragraphs && partialCount > 10"
                        :pageSlug="article['slug']"
                    />
                </div>

                <div
                    class="side-column"
                    v-if="showSidebar && !isMobile()"
                    :style="rankingSidebarStyles"
                >
                    <img
                        v-if="
                            (article.hero_image ||
                                $globalConstants.host.hero_banner_gw_deluxe) &&
                            sidebarHeroImage
                        "
                        v-lazy="articleHeroImage"
                        height="180"
                        width="300"
                        alt="hero image"
                        class="sidebar-hero-image"
                    />

                    <ContentsTable
                        v-if="article?.sub_headers"
                        :contents-items="article?.sub_headers"
                    />

                    <SidebarRanking :providers="rankingProviders" />
                </div>
            </div>
        </main>
    </div>
</template>

<script>
import { mediaQueriesMixin, contentDirectionMixin } from '@/ghost-mixins';
import SEOContentBreadcrumb from '@/components/SEOContentBreadcrumb';
import DateDisplay from '@atoms/DateDisplay/DateDisplay';
import PartialHandler from '@organisms/SEOArticlePartials/PartialHandler';
import ContentsTable from '@organisms/SEOArticlePartials/partials/ContentsTable';
import SocialMediaLinks from '@molecules/SocialMediaLinks/SocialMediaLinks';
import SidebarRanking from '@organisms/SEOArticlePartials/partials/SidebarRanking/SidebarRanking';
import AdsenseHeader from '@organisms/SEOArticlePartials/partials/AdsenseHeader.vue';

export default {
    name: 'ArticleDefault',
    components: {
        AdsenseHeader,
        DateDisplay,
        SEOContentBreadcrumb,
        PartialHandler,
        SocialMediaLinks,
        ContentsTable,
        SidebarRanking,
        BackToTop: () => '@organisms/SEOArticlePartials/partials/BackToTop',
        SeoArticleList: () => import('@/components/SeoArticleList.vue'),
    },
    data() {
        return {
            apiBaseUrl: window.__api_base_url__.replace(
                'v2',
                'ghost-writer/articles'
            ),
            partialCount: 0,
        };
    },
    mixins: [mediaQueriesMixin, contentDirectionMixin],
    props: {
        article: {
            type: Object,
        },
        titleColor: {
            type: String,
            default: '#000',
        },
        textColor: {
            type: String,
            default: '#000',
        },
        ctaButtonColor: {
            type: String,
            default: '#E36652',
        },
        ctaButtonTextColor: {
            type: String,
            default: '#FFFFFF',
        },
        dateTextColor: {
            type: String,
            default: '#000',
        },
        dateTextSizeMobile: {
            type: String,
            default: '12px',
        },
        dateTextSizeDesktop: {
            type: String,
            default: '12px',
        },
        showBreadcrumbs: {
            type: Boolean,
            default: true,
        },
        showSocialLinks: {
            type: Boolean,
            default: true,
        },
        showSidebar: {
            type: Boolean,
            default: true,
        },
        sidebarLocation: {
            type: String,
            default: 'left',
        },
        sidebarPartnersCount: {
            type: Number,
            default: 3,
        },
        stickyRankingSidebar: {
            type: Boolean,
            default: true,
        },
        showCarousel: {
            type: Boolean,
            default: true,
        },
        pageWidth: {
            type: String,
            default: '1200px',
        },
        sidebarHeroImage: {
            type: Boolean,
            default: false,
        },
        ArticleHeaderComponent: {
            type: String,
            default: 'ArticleHeaderA',
        },
    },
    computed: {
        articleAuthor() {
            if (this.article?.author) {
                return this.article['author'];
            }
            return null;
        },
        partialsArray() {
            if (this.article?.paragraphs.length > this.partialCount) {
                return this.article?.paragraphs.slice(0, this.partialCount);
            }
            return this.article?.paragraphs;
        },
        articleHeroImage() {
            return this.article.type === 'top5_home'
                ? `${this.$globalConstants.imageUrl}/${this.$globalConstants.host.hero_banner_gw_deluxe}?d=800x600&q=80`
                : `${this.article.hero_image}?d=800x600&q=80`;
        },
        articlePrimaryKeyword() {
            return this.article['primary_keyword'] || '';
        },
        layoutStyles() {
            if (this.showSidebar) {
                return this.sidebarLocation === 'left'
                    ? {
                          gridTemplateColumns: '1fr 2.6fr',
                          gridTemplateAreas: "'sidebar main'",
                      }
                    : {
                          gridTemplateColumns: '2.6fr 1fr',
                          gridTemplateAreas: "'main sidebar'",
                      };
            }
            return {};
        },
        rankingProviders() {
            if (!this.article['ranking_providers']) return [];
            return Object.values(this.article['ranking_providers']);
        },
        styleProps() {
            let textColor = this.$globalConstants.components.Settings
                ? this.$globalConstants.components.Settings['textColor']
                : '#333';

            return {
                color: textColor,
                '--theme-text-color': textColor,
            };
        },
        rankingSidebarStyles() {
            if (this.stickyRankingSidebar && !this.isMobile()) {
                return {
                    position: 'sticky',
                    top: '40px',
                };
            }
            return {};
        },
    },
    created() {
        // disable IntersectObserver for bot traffic
        if (window['__is_crawler__']) {
            this.partialCount = this.article?.paragraphs.length;
            return;
        }
        this.partialCount = this.isMobile() ? 4 : 8;

        // listen for content table click event
        this.$eventHub.$on('content-table-click', () => {
            if (this.partialCount < this.article?.paragraphs.length) {
                this.partialCount = this.article?.paragraphs.length;
            }
        });
    },
    beforeDestroy() {
        this.$eventHub.$off('content-table-click');
    },
};
</script>

<style lang="scss" scoped>
@import '@/sass/mixins';

.article-page {
    width: 100%;

    main {
        padding: 20px;
        max-width: 1180px;
        margin: auto auto 60px;
        display: flex;
        flex-direction: column;
        grid-row-gap: 20px;
        @include mq('tablet') {
            padding: 20px 40px;
        }

        .article-nav {
            display: flex;
            flex-direction: column;

            .nav-links {
                display: flex;
                align-items: center;
                justify-content: space-between;

                .link-back {
                    margin: 20px 0;
                    color: #8c8c8c;
                    font-size: 16px;
                    display: flex;
                    align-items: center;
                    .base-icon {
                        margin-right: 8px;
                    }
                }
            }
        }

        .article-content {
            display: flex;
            grid-column-gap: 40px;
            flex-direction: column;
            @include mq('tablet') {
                display: grid;
                align-items: flex-start;
            }

            .main-column {
                margin-bottom: 60px;
                grid-area: main;
                display: flex;
                flex-direction: column;
                min-height: 800px;

                .page-header {
                    flex-direction: column;
                    margin-bottom: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    @include mq('tablet') {
                        border-radius: 12px;
                    }

                    .page-title {
                        text-align: left;
                        font-size: 1.4em;
                        line-height: 1.3;
                        @include mq('tablet') {
                            font-size: 1.8em;
                        }
                    }
                }

                .review-logo {
                    display: flex;
                    justify-content: center;
                    padding: 20px;
                    align-self: center;

                    img {
                        max-height: 100px;
                        object-fit: contain;
                    }
                }

                .social-links {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    gap: 20px;
                    margin-top: 60px;

                    > p {
                        font-weight: bold;
                    }
                }
            }

            .side-column {
                grid-area: sidebar;
                display: flex;
                flex-direction: column;
                gap: 40px;

                .ranking-sidebar {
                    .ranking-title {
                        font-size: 20px;
                        margin-bottom: 18px;
                        width: 100%;
                        font-weight: 600;
                    }
                }

                .sidebar-hero-image {
                    width: 100%;
                    height: auto;
                    border-radius: 8px;
                    object-fit: contain;
                }
            }
        }

        .article-footer {
            width: 100%;
            margin-top: 40px;
        }
    }

    .main-partial-container {
        margin-top: 32px;
    }
}
</style>
